import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { XBoldIcon } from '@pattern-library/icon';
const propTypes = {
  /**
   * Duration of the close animation in milliseconds.
   */
  animationDuration: PropTypes.number,
  /**
   * Card background color
   */
  backgroundColor: PropTypes.oneOf(['brand', 'transparent', 'white', 'gray-1', 'gray-2', 'gradient-gray-05-1', 'gradient-gray-05-2', 'gradient-gray-10-1']),
  /**
   * Enables border style around card. Does not apply to formatted card.
   */
  border: PropTypes.bool,
  /**
   * **(Formatted card only)** Array of strings or elements that will render as line items
   **/
  bylines: PropTypes.array,
  /**
   * Content of the card
   */
  children: PropTypes.node,
  /**
   * Additional classes for the card
   */
  className: PropTypes.string,
  /**
   * Accessible label for close button (not visually rendered)
   */
  closeButtonLabel: PropTypes.string,
  /**
   * Any JSX element, like an &lt;img&gt; or &lt;div&gt; with styled background.
   */
  customBackground: PropTypes.element,
  /**
   * Disables responsive styles when true
   */
  desktopOnly: PropTypes.bool,
  /**
   * Display drop shadow
   */
  dropShadow: PropTypes.bool,
  /**
   * Adds a close button to the card. To set a custom color for the close button icon,
   * override the CSS `color` property for the `asc-card-close-btn` class.
   */
  hasCloseButton: PropTypes.bool,
  /**
   * Callback function for close button click
   */
  onClose: PropTypes.func,
  /**
   * Adds padding to the card. Default is `true`. Can be set to `false` for no padding.
   * Can also be set to `'fluid'` for dynamic padding relative to half a column's width.
   * Does not apply to formatted card.
   */
  padding: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /**
   * Identifier for automated tests added to the `data-qa` HTML attribute
   **/
  qaId: PropTypes.string,
  /**
   * **(Formatted card only)** Second line of copy
   **/
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /**
   * **(Formatted card only)** First line of copy. When provided, the card layout will be formatted.
   **/
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /**
   * Enables closing animation
   */
  useAnimation: PropTypes.bool
};
const defaultProps = {
  animationDuration: 400,
  backgroundColor: 'transparent',
  border: false,
  bylines: [],
  className: '',
  closeButtonLabel: 'Dismiss',
  desktopOnly: false,
  hasCloseButton: false,
  onClose: () => {},
  padding: true,
  qaId: '',
  subtitle: '',
  useAnimation: false
};
const getFormattedLayout = (title, subtitle, bylines) => {
  return /*#__PURE__*/React.createElement(React.Fragment, null, title && /*#__PURE__*/React.createElement("p", {
    className: "asc-card-title"
  }, title), subtitle && /*#__PURE__*/React.createElement("p", {
    className: "asc-card-subtitle"
  }, subtitle), bylines.length > 0 && bylines.map((byline, index) => {
    return /*#__PURE__*/React.createElement("p", {
      key: index,
      className: "asc-card-byline"
    }, byline);
  }));
};
const getCustomBackground = (customBackground, children) => {
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.cloneElement(customBackground, {
    className: classnames('asc-card--custom-bg', customBackground.props.className)
  }), /*#__PURE__*/React.createElement("div", {
    className: "asc-card-contents"
  }, children));
};
function Card(props) {
  let {
    animationDuration,
    backgroundColor,
    border,
    children,
    className,
    closeButtonLabel,
    customBackground,
    desktopOnly,
    dropShadow,
    hasCloseButton,
    onClose,
    padding,
    title,
    subtitle,
    bylines,
    qaId,
    useAnimation
  } = props;
  const formattedCard = title !== undefined;
  const [isClosing, setIsClosing] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const handleClose = event => {
    const delay = useAnimation ? animationDuration : 0;
    setIsClosing(true);
    window.setTimeout(() => {
      setIsClosed(true);
      setIsClosing(false);
      onClose(event);
    }, delay);
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classnames('asc-card', `asc-card--bg-${backgroundColor}`, className, {
      'asc-card--desktop-only': desktopOnly,
      'asc-card--shadow': dropShadow,
      'asc-card--padding': padding === true && !formattedCard,
      'asc-card--padding-fluid': padding === 'fluid' && !formattedCard,
      'asc-card--formatted': formattedCard,
      'asc-card--border': border,
      'asc-card--closing': isClosing,
      'asc-card--closed': isClosed,
      'asc-card--animate': useAnimation
    }),
    style: {
      transitionDuration: useAnimation ? `${animationDuration}ms` : null
    },
    "data-qa": qaId
  }, title && getFormattedLayout(title, subtitle, bylines), customBackground && children ? getCustomBackground(customBackground, children) : children, hasCloseButton && /*#__PURE__*/React.createElement("button", {
    className: "asc-card-close-btn",
    type: "button",
    onClick: handleClose
  }, /*#__PURE__*/React.createElement(XBoldIcon, {
    size: "xs",
    color: "current"
  }), /*#__PURE__*/React.createElement("span", {
    className: "asc-acc-hidden"
  }, closeButtonLabel)));
}
Card.propTypes = propTypes;
Card.defaultProps = defaultProps;
export default Card;